/**
 * The one place the browser talks to the Happy Fishy API.
 *
 * VITE_API_BASE_URL defined (even as an empty string) turns sync on. Empty
 * means same origin, which is how nginx and the Vite dev proxy serve it.
 * Absent means there is no server and everything stays local.
 */
const raw = import.meta.env.VITE_API_BASE_URL as string | undefined;

export const syncConfigured = raw !== undefined;
export const API_BASE = (raw ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

/** JSON in, JSON out, cookies included. Throws ApiError on non-2xx. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (
    init.body &&
    typeof init.body === 'string' &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(apiUrl(path), {
    ...init,
    headers,
    credentials: 'include',
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // not JSON; keep the status message
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
